import { Composable, Inject, Setup, Vue } from '@natureos/vue-facing-decorator';

import type { ApiResponse, ApiService } from '@/common/services/api.service-new';
import type { MultiPolygonCoords } from '../hansen/hansen.types';
import { ESA_CCI_DEFAULT_YEAR } from './esa-cci.const';
import { useEsaCciStore } from './esa-cci.store';
import type {
  EsaCciChangeResponse,
  EsaCciLayerResponse,
  EsaCciScheme,
  EsaCciStatsResponse,
  EsaCciTimeseriesResponse,
} from './esa-cci.types';

/**
 * ESA CCI / Copernicus C3S Land Cover (300 m, IPCC-categorised) — sibling of
 * `useLulcService` with an added `scheme` (ipcc | lccs) dimension. Tiles are
 * served from JWT-protected `/esa-cci/<scheme>/tiles/{z}/{x}/{y}?year=`
 * endpoints (Lambda@Edge verifies the bearer); `ApiService` attaches the token
 * on the layer/stats/change calls, and the tile fetcher routes through the
 * raster service so the bearer rides on the tile requests too.
 *
 * Contract: ~/.claude/contract/260629_esa_cci_lulc.md
 */
@Composable()
export class useEsaCciService extends Vue {
  @Inject()
  private readonly api!: ApiService;

  @Setup(() => useEsaCciStore())
  public readonly esaCciStore!: ReturnType<typeof useEsaCciStore>;

  /**
   * Layer metadata for `year` — both tile-URL variants, both legends, and the
   * attribution string in one payload. Cached per year in `esaCciStore`;
   * `signal` only matters on the cache-miss path since the cached return is
   * synchronous.
   */
  public async getEsaCciLayer(
    year: number = ESA_CCI_DEFAULT_YEAR,
    signal?: AbortSignal,
  ): Promise<ApiResponse<EsaCciLayerResponse>> {
    const cached = this.esaCciStore.getLayer(year);
    if (cached) {
      return cached;
    }

    const res = await this.api.get<EsaCciLayerResponse>(
      `${import.meta.env.VITE_GEE_API}/esa-cci/layer?year=${year}`,
      { signal },
    );

    if (res.isSuccess && res.data) {
      this.esaCciStore.setLayer(year, res);
    }
    return res;
  }

  /** Per-class hectares in `coords` for the scheme at `year`. */
  public async getEsaCciStats(
    coords: MultiPolygonCoords,
    year: number = ESA_CCI_DEFAULT_YEAR,
    scheme: EsaCciScheme = 'ipcc',
    signal?: AbortSignal,
  ): Promise<ApiResponse<EsaCciStatsResponse>> {
    return this.api.post<EsaCciStatsResponse>(
      `${import.meta.env.VITE_GEE_API}/esa-cci/stats?year=${year}&scheme=${scheme}`,
      { data: coords },
      { signal },
    );
  }

  /** Year-to-year transition matrix + net change for `coords` between `from` and `to`. */
  public async getEsaCciChange(
    coords: MultiPolygonCoords,
    from: number,
    to: number,
    scheme: EsaCciScheme = 'ipcc',
    signal?: AbortSignal,
  ): Promise<ApiResponse<EsaCciChangeResponse>> {
    return this.api.post<EsaCciChangeResponse>(
      `${import.meta.env.VITE_GEE_API}/esa-cci/change?from=${from}&to=${to}&scheme=${scheme}`,
      { data: coords },
      { signal },
    );
  }

  /**
   * Per-class hectares for EVERY year in `coords` — powers the stacked-area
   * yearly-distribution chart. One call returns the full 2000–2022 series.
   */
  public async getEsaCciTimeseries(
    coords: MultiPolygonCoords,
    scheme: EsaCciScheme = 'ipcc',
    signal?: AbortSignal,
  ): Promise<ApiResponse<EsaCciTimeseriesResponse>> {
    return this.api.post<EsaCciTimeseriesResponse>(
      `${import.meta.env.VITE_GEE_API}/esa-cci/timeseries?scheme=${scheme}`,
      { data: coords },
      { signal },
    );
  }
}
